import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../stores/useAppStore';
import { usePlayerV2 } from '../stores/usePlayerV2';
import {
  abortStory,
  backgroundExtractCanon,
  generateAudio,
  generateOutline,
  generateProse,
  outlineToText,
  reviseOutline,
  reviseProse,
} from '../pipeline/stages';
import { prose as proseStore, audio as audioStore, outlines as outlineStore } from '../storage/db';
import { isSyncConfigured } from '../lib/syncClient';
import { listVoices, isVoiceCompatibleWith, type ElevenVoice } from '../pipeline/elevenlabs';
import StreamingTextView from '../components/StreamingTextView';
import { useWakeLock } from '../lib/useWakeLock';
import type { Story, Universe } from '../types';
import './routes.css';

function audioFilename(title: string): string {
  const safe = title
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .slice(0, 60) || 'story';
  return `${safe}.mp3`;
}

async function shareOrDownloadAudio(blob: Blob, title: string): Promise<void> {
  // ElevenLabs returns audio/mpeg (MP3) — natively playable on iOS, Android,
  // and WhatsApp. Force the mime in case the blob was stored without one.
  const mp3 = blob.type === 'audio/mpeg' ? blob : new Blob([blob], { type: 'audio/mpeg' });
  const filename = audioFilename(title);
  const file = new File([mp3], filename, { type: 'audio/mpeg' });
  const nav = navigator as Navigator & {
    canShare?: (data: ShareData) => boolean;
  };

  if (typeof nav.share === 'function') {
    if (!nav.canShare?.({ files: [file] })) {
      throw new Error(
        "This browser can't share audio files. Try Safari (iOS) or Chrome (Android)."
      );
    }
    // Files only. Passing `text` or `title` causes some receiving apps
    // (WhatsApp on iOS in particular) to forward the text as a message
    // and silently drop the attachment.
    await nav.share({ files: [file] });
    return;
  }

  // Desktop fallback — actually downloads to disk.
  const url = URL.createObjectURL(mp3);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function formatAudioAge(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < 10_000) return 'just now';
  if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
  const min = Math.floor(diff / 60_000);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  return `${days}d ago`;
}

export default function WorkspaceRoute() {
  const activeUniverseId = useAppStore((s) => s.ui.activeUniverseId);
  const universes = useAppStore((s) => s.universes);
  const activeStoryByUniverse = useAppStore((s) => s.ui.activeStoryByUniverse);
  const stories = useAppStore((s) => s.stories);
  const nav = useNavigate();

  const universe = activeUniverseId ? universes[activeUniverseId] : undefined;
  const activeId = activeUniverseId ? activeStoryByUniverse[activeUniverseId] : undefined;
  const story = activeId ? stories[activeId] : undefined;

  const visibleStories = useMemo(
    () =>
      Object.values(stories)
        .filter((s) => !s.hidden && s.universeId === activeUniverseId)
        .sort((a, b) => b.updatedAt - a.updatedAt),
    [stories, activeUniverseId]
  );

  if (!universe) {
    return (
      <div className="route">
        <h1>Workspace</h1>
        <div className="dim">No active universe. Pick one from the home screen.</div>
        <button onClick={() => nav('/')}>Go to universes</button>
      </div>
    );
  }

  if (!story) {
    return (
      <div className="route">
        <h1>Workspace</h1>
        <div className="dim">
          No active story. Pick one from{' '}
          <a onClick={() => nav('/slugs')} style={{ cursor: 'pointer' }}>
            Story Ideas
          </a>
          {visibleStories.length > 0 && <> or resume one below.</>}
        </div>
        {visibleStories.length > 0 && (
          <div className="story-picker">
            {visibleStories.map((s) => (
              <StoryPickerRow key={s.id} story={s} universeId={universe.id} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return <StoryWorkspace story={story} universe={universe} />;
}

function StoryPickerRow({ story, universeId }: { story: Story; universeId: string }) {
  const setActive = useAppStore((s) => s.setActiveStoryForUniverse);
  const nav = useNavigate();
  return (
    <button
      className="story-picker-row"
      onClick={() => {
        setActive(universeId, story.id);
        nav('/workspace');
      }}
    >
      <div>
        <div className="slug-title">{story.slug.title}</div>
        <div className="dim small">{story.stage}</div>
      </div>
    </button>
  );
}

function StoryWorkspace({ story, universe }: { story: Story; universe: Universe }) {
  const settings = useAppStore((s) => s.settings);
  const setStoryOutline = useAppStore((s) => s.setStoryOutline);
  const setStoryProseKey = useAppStore((s) => s.setStoryProseKey);
  const setStoryAudio = useAppStore((s) => s.setStoryAudio);
  const setInFlight = useAppStore((s) => s.setInFlight);
  const updateStory = useAppStore((s) => s.updateStory);
  const appendRevision = useAppStore((s) => s.appendRevision);
  const setStoryVoiceOverride = useAppStore((s) => s.setStoryVoiceOverride);
  const setActive = useAppStore((s) => s.setActiveStoryForUniverse);
  const playV2 = usePlayerV2((s) => s.play);
  const playingId = usePlayerV2((s) => s.storyId);
  const playerStatus = usePlayerV2((s) => s.status);

  const [proseText, setProseText] = useState<string>('');
  const [proseStreaming, setProseStreaming] = useState(false);
  const [outlineBusy, setOutlineBusy] = useState(false);
  const [audioBusy, setAudioBusy] = useState(false);

  useWakeLock(proseStreaming || outlineBusy || audioBusy, 'workspace');
  const [error, setError] = useState<string | undefined>();

  const [outlineEdit, setOutlineEdit] = useState<string>('');
  const [outlineNotes, setOutlineNotes] = useState('');
  const [proseNotes, setProseNotes] = useState('');

  const [voices, setVoices] = useState<ElevenVoice[]>([]);
  const canGenerate = isSyncConfigured();

  // Load persisted prose on mount/change
  useEffect(() => {
    let cancelled = false;
    if (story.proseKey) {
      proseStore.get(story.proseKey).then((t) => {
        if (!cancelled && t) setProseText(t);
      });
    } else {
      setProseText('');
    }
    return () => {
      cancelled = true;
    };
  }, [story.proseKey]);

  useEffect(() => {
    setOutlineEdit(
      story.outlineText ?? (story.outline ? outlineToText(story.outline) : '')
    );
  }, [story.outlineText, story.outline]);

  useEffect(() => {
    if (!canGenerate) return;
    let cancelled = false;
    listVoices()
      .then((vs) => {
        if (!cancelled) setVoices(vs);
      })
      .catch(() => {
        /* fine — fall back to manual id entry */
      });
    return () => {
      cancelled = true;
    };
  }, [canGenerate]);

  async function onGenerateOutline() {
    setError(undefined);
    setOutlineBusy(true);
    setInFlight(story.id, 'outline');
    try {
      const { raw, beats } = await generateOutline({
        storyId: story.id,
        universeId: universe.id,
        settings,
        slug: story.slug,
      });
      await outlineStore.set(story.id, raw);
      setStoryOutline(story.id, beats, raw);
      setOutlineEdit(raw);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setOutlineBusy(false);
      setInFlight(story.id, undefined);
    }
  }

  async function onReviseOutline() {
    if (!outlineNotes.trim()) return;
    setError(undefined);
    setOutlineBusy(true);
    setInFlight(story.id, 'outline');
    try {
      const { raw, beats } = await reviseOutline({
        storyId: story.id,
        universeId: universe.id,
        settings,
        slug: story.slug,
        currentOutline: outlineEdit,
        notes: outlineNotes,
      });
      await outlineStore.set(story.id, raw);
      setStoryOutline(story.id, beats, raw);
      appendRevision(story.id, 'outline', outlineNotes);
      setOutlineEdit(raw);
      setOutlineNotes('');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setOutlineBusy(false);
      setInFlight(story.id, undefined);
    }
  }

  function onSaveOutlineEdits() {
    updateStory(story.id, { outlineText: outlineEdit });
    outlineStore.set(story.id, outlineEdit);
  }

  async function onGenerateProse() {
    if (!outlineEdit.trim()) {
      setError('Need an outline first');
      return;
    }
    setError(undefined);
    setProseText('');
    setProseStreaming(true);
    setInFlight(story.id, 'prose');
    try {
      const full = await generateProse({
        storyId: story.id,
        universeId: universe.id,
        settings,
        outline: outlineEdit,
        slug: story.slug,
        onChunk: (chunk) => setProseText((prev) => prev + chunk),
      });
      await proseStore.set(story.id, full);
      setStoryProseKey(story.id, story.id);
      void backgroundExtractCanon(universe.id, settings, full, story.id);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setProseStreaming(false);
      setInFlight(story.id, undefined);
    }
  }

  async function onReviseProse() {
    if (!proseNotes.trim()) return;
    setError(undefined);
    let next = '';
    setProseText('');
    setProseStreaming(true);
    setInFlight(story.id, 'prose');
    try {
      const full = await reviseProse({
        storyId: story.id,
        universeId: universe.id,
        settings,
        slug: story.slug,
        currentProse: (await proseStore.get(story.id)) ?? proseText,
        notes: proseNotes,
        onChunk: (chunk) => {
          next += chunk;
          setProseText(next);
        },
      });
      await proseStore.set(story.id, full);
      setStoryProseKey(story.id, story.id);
      appendRevision(story.id, 'prose', proseNotes);
      setProseNotes('');
      void backgroundExtractCanon(universe.id, settings, full, story.id);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setProseStreaming(false);
      setInFlight(story.id, undefined);
    }
  }

  async function onSaveProseEdits() {
    await proseStore.set(story.id, proseText);
    setStoryProseKey(story.id, story.id);
  }

  async function onShareAudio() {
    if (!story.audio) return;
    const blob = await audioStore.get(story.audio.blobKey);
    if (!blob) {
      setError('Audio file is missing — try regenerating.');
      return;
    }
    try {
      await shareOrDownloadAudio(blob, story.slug.title);
    } catch (e) {
      if ((e as Error).name === 'AbortError') return;
      setError((e as Error).message);
    }
  }

  async function onGenerateAudio() {
    const text = (await proseStore.get(story.id)) ?? proseText;
    if (!text.trim()) {
      setError('Need prose first');
      return;
    }
    setError(undefined);
    setAudioBusy(true);
    setInFlight(story.id, 'tts');
    try {
      const { mime, voiceId } = await generateAudio({
        story,
        settings,
        prose: text,
        title: story.slug.title,
      });
      setStoryAudio(story.id, {
        id: story.id,
        blobKey: story.id,
        mimeType: mime || 'audio/mpeg',
        voiceId,
        createdAt: Date.now(),
      });
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setAudioBusy(false);
      setInFlight(story.id, undefined);
    }
  }

  function onCancel() {
    abortStory(story.id);
    setInFlight(story.id, undefined);
    setProseStreaming(false);
    setOutlineBusy(false);
    setAudioBusy(false);
  }

  const hasOutline = !!outlineEdit.trim();
  const hasProse = !!proseText.trim() || !!story.proseKey;
  const hasAudio = !!story.audio;
  const effectiveVoiceId = story.voiceIdOverride || universe.defaultVoiceId;
  const hasVoice = !!effectiveVoiceId;

  function voiceName(id: string | undefined): string {
    if (!id) return '(none)';
    const v = voices.find((x) => x.voice_id === id);
    return v?.name ?? id;
  }

  return (
    <div className="route">
      <div className="workspace-header">
        <div>
          <h1>{story.slug.title}</h1>
          <div className="dim small">{story.slug.hook}</div>
          {universe.canonInFlight && (
            <div className="dim small" style={{ marginTop: 4 }}>
              Updating canon…
            </div>
          )}
        </div>
        <button className="ghost" onClick={() => setActive(universe.id, undefined)}>
          Back to list
        </button>
      </div>

      {error && <div className="error">{error}</div>}
      {story.inFlight && (
        <div className="inflight">
          Working on: {story.inFlight}{' '}
          <button className="ghost" onClick={onCancel}>
            Cancel
          </button>
        </div>
      )}

      {/* Outline stage */}
      <section className="stage-card">
        <h2>Outline</h2>
        {!hasOutline && !outlineBusy && (
          <button className="primary" onClick={onGenerateOutline}>
            Generate outline
          </button>
        )}
        {outlineBusy && <div className="dim">Thinking…</div>}
        {hasOutline && (
          <>
            <textarea
              rows={10}
              value={outlineEdit}
              onChange={(e) => setOutlineEdit(e.target.value)}
              onBlur={onSaveOutlineEdits}
            />
            <div className="row">
              <button onClick={onGenerateOutline} disabled={outlineBusy}>
                Regenerate
              </button>
            </div>
            <div className="revise-row">
              <input
                placeholder="Revision notes (e.g., 'make the ending quieter')"
                value={outlineNotes}
                onChange={(e) => setOutlineNotes(e.target.value)}
              />
              <button
                onClick={onReviseOutline}
                disabled={outlineBusy || !outlineNotes.trim()}
              >
                Revise
              </button>
            </div>
          </>
        )}
      </section>

      {/* Prose stage */}
      {hasOutline && (
        <section className="stage-card">
          <h2>Prose</h2>
          {!hasProse && !proseStreaming && (
            <button className="primary" onClick={onGenerateProse}>
              Generate prose
            </button>
          )}
          {(proseStreaming || hasProse) && (
            <>
              <StreamingTextView text={proseText} streaming={proseStreaming} />
              {!proseStreaming && (
                <>
                  <textarea
                    rows={10}
                    value={proseText}
                    onChange={(e) => setProseText(e.target.value)}
                    onBlur={onSaveProseEdits}
                  />
                  <div className="row">
                    <button onClick={onGenerateProse} disabled={proseStreaming}>
                      Regenerate
                    </button>
                  </div>
                  <div className="revise-row">
                    <input
                      placeholder="Revision notes (e.g., 'soften the middle')"
                      value={proseNotes}
                      onChange={(e) => setProseNotes(e.target.value)}
                    />
                    <button
                      onClick={onReviseProse}
                      disabled={proseStreaming || !proseNotes.trim()}
                    >
                      Revise
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </section>
      )}

      {/* Audio stage */}
      {hasProse && !proseStreaming && (
        <section className="stage-card">
          <h2>Audio</h2>
          <label>
            <div className="label">
              Voice{' '}
              {story.voiceIdOverride ? (
                <span className="tag tag-soft" style={{ marginLeft: 6 }}>story override</span>
              ) : (
                <span className="dim small" style={{ marginLeft: 6 }}>
                  using universe default ({voiceName(universe.defaultVoiceId)})
                </span>
              )}
            </div>
            {voices.length > 0 ? (
              <>
                <select
                  value={story.voiceIdOverride ?? ''}
                  onChange={(e) => setStoryVoiceOverride(story.id, e.target.value || undefined)}
                >
                  <option value="">— use universe default —</option>
                  {[...voices]
                    .sort((a, b) => {
                      const aV3 = isVoiceCompatibleWith(a, settings.ttsModel);
                      const bV3 = isVoiceCompatibleWith(b, settings.ttsModel);
                      if (aV3 !== bV3) return aV3 ? -1 : 1;
                      return a.name.localeCompare(b.name);
                    })
                    .map((v) => {
                      const compat = isVoiceCompatibleWith(v, settings.ttsModel);
                      return (
                        <option key={v.voice_id} value={v.voice_id}>
                          {compat ? '✓ ' : '· '}
                          {v.name}
                          {!compat ? ` — not tuned for ${settings.ttsModel}` : ''}
                        </option>
                      );
                    })}
                </select>
                {effectiveVoiceId &&
                  (() => {
                    const v = voices.find((x) => x.voice_id === effectiveVoiceId);
                    if (!v) return null;
                    if (isVoiceCompatibleWith(v, settings.ttsModel)) return null;
                    return (
                      <div className="warn small">
                        "{v.name}" isn't tuned for <code>{settings.ttsModel}</code> —
                        performance tags will be read aloud as words. Pick a ✓ voice
                        or change the TTS model in Settings.
                      </div>
                    );
                  })()}
              </>
            ) : (
              <input
                value={story.voiceIdOverride ?? ''}
                onChange={(e) => setStoryVoiceOverride(story.id, e.target.value || undefined)}
                placeholder="(leave blank to use universe default)"
              />
            )}
          </label>

          {!hasAudio && !audioBusy && (
            <button
              className="primary"
              onClick={onGenerateAudio}
              disabled={!canGenerate || !hasVoice}
            >
              Generate audio
            </button>
          )}
          {audioBusy && <div className="dim">Synthesizing…</div>}
          {hasAudio && story.audio && (
            <div className="row">
              <div className="dim">
                Audio ready · {formatAudioAge(story.audio.createdAt)}
              </div>
              <button
                className="primary"
                onClick={() => void playV2(story.id)}
              >
                {playingId === story.id && playerStatus === 'playing'
                  ? 'Playing'
                  : playingId === story.id &&
                    (playerStatus === 'resolving' ||
                      playerStatus === 'fetching' ||
                      playerStatus === 'buffering')
                  ? 'Loading…'
                  : 'Play'}
              </button>
              <button onClick={onShareAudio} disabled={audioBusy}>
                Share
              </button>
              <button onClick={onGenerateAudio} disabled={audioBusy}>
                Regenerate audio
              </button>
            </div>
          )}
          {!canGenerate && (
            <div className="warn">Configure sync in Settings to generate audio.</div>
          )}
          {canGenerate && !hasVoice && (
            <div className="warn">
              Set a default voice in Universe settings, or pick one above for this story.
            </div>
          )}
        </section>
      )}
    </div>
  );
}
